import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  CircularProgress, Chip, Stack, Alert, Paper, Divider,
  Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, LinearProgress, Tab, Tabs,
  InputAdornment
} from '@mui/material';
import {
  HowToReg, Login, Upload, CheckCircle, Schedule, Assignment,
  Visibility, VisibilityOff, AddCircle, Delete, OpenInNew
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const api = axios.create({ baseURL: API_BASE });

const STATUS_CFG = {
  draft:               { label: 'Draft — Not Yet Submitted', color: 'default',   step: 0 },
  submitted:           { label: 'Submitted — Awaiting Review', color: 'info',    step: 1 },
  under_review:        { label: 'Under Review', color: 'warning',                step: 2 },
  shortlisted:         { label: '✓ Shortlisted!', color: 'primary',              step: 3 },
  interview_scheduled: { label: 'Interview Scheduled', color: 'secondary',       step: 3 },
  interviewed:         { label: 'Interviewed', color: 'warning',                 step: 4 },
  accepted:            { label: '🎉 Accepted!', color: 'success',                step: 5 },
  rejected:            { label: 'Application Unsuccessful', color: 'error',      step: 5 },
  withdrawn:           { label: 'Withdrawn', color: 'default',                   step: 0 },
};
const STEPS = ['Submitted','Under Review','Shortlisted','Interview','Decision'];
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';

// ── Auth section ───────────────────────────────────────────────────────────────
function AuthSection({ onLogin }) {
  const [mode, setMode]     = useState('login'); // login | register
  const [form, setForm]     = useState({ email:'', password:'', first_name:'', last_name:'', phone:'' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      const url = mode === 'login' ? '/admissions/applicant/login' : '/admissions/applicant/register';
      const r = await api.post(url, form);
      if (r.data.success) {
        if (mode === 'login') {
          localStorage.setItem('applicant_token', r.data.token);
          onLogin(r.data.user, r.data.token);
        } else {
          setMode('login');
          setError(''); alert('Account created! Please log in.');
        }
      }
    } catch (e) { setError(e.response?.data?.message || 'An error occurred'); }
    finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', bgcolor:'#f5f6fa', p:2 }}>
      <Card variant="outlined" sx={{ maxWidth:440, width:'100%', borderRadius:3 }}>
        <CardContent sx={{ p:4 }}>
          <Stack alignItems="center" sx={{ mb:3 }}>
            <HowToReg sx={{ fontSize:48, color:'#0f2b4a', mb:1 }} />
            <Typography variant="h5" fontWeight={800} color="#0f2b4a">Admissions Portal</Typography>
            <Typography variant="body2" color="text.secondary">Apply for a place at our school</Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb:3 }}>
            {['login','register'].map(m => (
              <Button key={m} fullWidth variant={mode===m?'contained':'outlined'}
                onClick={() => setMode(m)} sx={{ bgcolor: mode===m ? '#0f2b4a' : undefined }}>
                {m === 'login' ? 'Log In' : 'Create Account'}
              </Button>
            ))}
          </Stack>

          {error && <Alert severity="error" sx={{ mb:2, fontSize:12 }}>{error}</Alert>}

          <Stack spacing={2}>
            {mode === 'register' && (
              <Stack direction="row" spacing={1}>
                <TextField fullWidth size="small" label="First Name *" value={form.first_name} onChange={setF('first_name')} />
                <TextField fullWidth size="small" label="Last Name *" value={form.last_name} onChange={setF('last_name')} />
              </Stack>
            )}
            <TextField fullWidth size="small" label="Email Address *" type="email" value={form.email} onChange={setF('email')} />
            {mode === 'register' && (
              <TextField fullWidth size="small" label="Phone Number" value={form.phone} onChange={setF('phone')} />
            )}
            <TextField fullWidth size="small" label="Password *" value={form.password} onChange={setF('password')}
              type={showPw ? 'text' : 'password'}
              InputProps={{ endAdornment:
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw(v => !v)}>
                    {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              }} />
            <Button fullWidth variant="contained" size="large" onClick={submit} disabled={loading}
              sx={{ bgcolor:'#0f2b4a', py:1.5, fontWeight:700 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : mode === 'login' ? 'Log In' : 'Create Account'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Application Form ───────────────────────────────────────────────────────────
function ApplicationForm({ intake, existingApp, token, onSaved, onBack }) {
  const [form, setForm] = useState({
    intake_id: intake.id,
    candidate_first_name: '', candidate_last_name: '', candidate_dob: '',
    candidate_gender: '', candidate_nationality: '', previous_school: '', previous_grade: '',
    guardian_name: '', guardian_phone: '', guardian_email: '', guardian_relationship: '',
    motivation_statement: '', how_did_you_hear: '',
    ...existingApp
  });
  const [docs, setDocs]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [docType, setDocType] = useState('');
  const [file, setFile]       = useState(null);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const authApi = axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (existingApp?.id) {
      authApi.get(`/admissions/applications/${existingApp.id}/documents`)
        .then(r => { if (r.data.success) setDocs(r.data.data); });
    }
  }, [existingApp?.id]);

  const save = async (submit = false) => {
    setError(''); setSaving(true);
    try {
      await authApi.post('/admissions/apply', { ...form, action: submit ? 'submit' : 'save' });
      onSaved(submit);
    } catch (e) { setError(e.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const uploadDoc = async () => {
    if (!file || !docType) return;
    if (!existingApp?.id) { alert('Please save your application first.'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', docType);
    try {
      await authApi.post(`/admissions/applications/${existingApp.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await authApi.get(`/admissions/applications/${existingApp.id}/documents`);
      if (r.data.success) setDocs(r.data.data);
      setFile(null); setDocType('');
    } catch (e) { alert(e.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const deleteDoc = async (docId) => {
    try {
      await authApi.delete(`/admissions/applications/doc/${docId}`);
      setDocs(docs.filter(d => d.id !== docId));
    } catch { alert('Could not delete'); }
  };

  const submitted = existingApp?.status && existingApp.status !== 'draft';

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', p: 2 }}>
      <Button size="small" onClick={onBack} sx={{ mb:2 }}>← Back to Intakes</Button>
      <Card variant="outlined" sx={{ borderRadius:2, mb:2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography fontWeight={700} fontSize={16}>{intake.title}</Typography>
              <Typography fontSize={12} color="text.secondary">Grade: {intake.grade_applying} · Closes: {fmtD(intake.close_date)}</Typography>
            </Box>
            {existingApp?.status && <Chip label={STATUS_CFG[existingApp.status]?.label} color={STATUS_CFG[existingApp.status]?.color} size="small" sx={{ fontWeight:700 }} />}
          </Stack>
        </CardContent>
      </Card>

      {submitted && (
        <Alert severity="info" sx={{ mb:2 }}>
          This application has been submitted and can no longer be edited.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ borderRadius:2, mb:2 }}>
        <CardContent>
          <Typography fontWeight={700} gutterBottom>Candidate Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="First Name *" value={form.candidate_first_name} onChange={setF('candidate_first_name')} disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Last Name *" value={form.candidate_last_name} onChange={setF('candidate_last_name')} disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Date of Birth" value={form.candidate_dob||''} onChange={setF('candidate_dob')} InputLabelProps={{ shrink:true }} disabled={submitted} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Gender" value={form.candidate_gender||''} onChange={setF('candidate_gender')} disabled={submitted}>
                <MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem><MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Nationality" value={form.candidate_nationality||''} onChange={setF('candidate_nationality')} disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Previous School" value={form.previous_school||''} onChange={setF('previous_school')} disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Previous Grade / Class" value={form.previous_grade||''} onChange={setF('previous_grade')} disabled={submitted} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius:2, mb:2 }}>
        <CardContent>
          <Typography fontWeight={700} gutterBottom>Parent / Guardian</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Full Name" value={form.guardian_name||''} onChange={setF('guardian_name')} disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Relationship" value={form.guardian_relationship||''} onChange={setF('guardian_relationship')} placeholder="Mother, Father, Guardian…" disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Phone" value={form.guardian_phone||''} onChange={setF('guardian_phone')} disabled={submitted} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email" type="email" value={form.guardian_email||''} onChange={setF('guardian_email')} disabled={submitted} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius:2, mb:2 }}>
        <CardContent>
          <Typography fontWeight={700} gutterBottom>Motivation Statement</Typography>
          <TextField fullWidth size="small" multiline rows={5} value={form.motivation_statement||''}
            onChange={setF('motivation_statement')} disabled={submitted}
            placeholder="Tell us why you want to join this school. What are the candidate's strengths? What are their goals?" />
        </CardContent>
      </Card>

      {/* Document uploads */}
      <Card variant="outlined" sx={{ borderRadius:2, mb:2 }}>
        <CardContent>
          <Typography fontWeight={700} gutterBottom>Supporting Documents</Typography>
          {intake.requirements && (
            <Alert severity="info" sx={{ mb:2, fontSize:12, whiteSpace:'pre-line' }}>
              <strong>Required documents:</strong>{'\n'}{intake.requirements}
            </Alert>
          )}
          {docs.map(doc => (
            <Stack key={doc.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py:0.6, borderBottom:'1px solid #f5f5f5' }}>
              <Box>
                <Typography fontSize={13} fontWeight={600}>{doc.doc_type}</Typography>
                <Typography fontSize={11} color="text.secondary">{doc.file_name} · {(doc.file_size/1024).toFixed(1)} KB</Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {doc.verified && <Chip label="Verified" size="small" color="success" sx={{ fontSize:10 }} />}
                {!submitted && <IconButton size="small" color="error" onClick={() => deleteDoc(doc.id)}><Delete fontSize="small" /></IconButton>}
              </Stack>
            </Stack>
          ))}

          {!submitted && existingApp?.id && (
            <Stack direction="row" spacing={1} sx={{ mt:2 }} alignItems="center" flexWrap="wrap">
              <TextField size="small" label="Document Type" value={docType} onChange={e => setDocType(e.target.value)}
                placeholder="e.g. Birth Certificate" sx={{ minWidth:180 }} />
              <Button variant="outlined" component="label" startIcon={<Upload />} size="small">
                Choose File <input hidden type="file" onChange={e => setFile(e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              </Button>
              {file && <Typography fontSize={12} color="text.secondary">{file.name}</Typography>}
              <Button variant="contained" size="small" onClick={uploadDoc} disabled={uploading || !file || !docType} sx={{ bgcolor:'#0f2b4a' }}>
                {uploading ? <CircularProgress size={16} color="inherit" /> : 'Upload'}
              </Button>
            </Stack>
          )}
          {!submitted && !existingApp?.id && (
            <Alert severity="warning" sx={{ mt:1, fontSize:12 }}>Save your application first, then you can upload documents.</Alert>
          )}
        </CardContent>
      </Card>

      {!submitted && (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => save(false)} disabled={saving}>Save Draft</Button>
          <Button variant="contained" onClick={() => { if (window.confirm('Submit this application? You will not be able to edit it after submission.')) save(true); }}
            disabled={saving} sx={{ bgcolor:'#0f2b4a' }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Submit Application'}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

// ── My Applications dashboard ──────────────────────────────────────────────────
function MyApplications({ token, onApply, onEdit }) {
  const [apps, setApps]         = useState([]);
  const [intakes, setIntakes]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const authApi = axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    Promise.all([
      authApi.get('/admissions/my-applications'),
      api.get('/admissions/intakes/public')
    ]).then(([ar, ir]) => {
      if (ar.data.success) setApps(ar.data.data);
      if (ir.data.success) setIntakes(ir.data.data);
    }).finally(() => setLoading(false));
  }, []);

  const openIntakes = intakes.filter(i => !apps.some(a => a.intake_id === i.id));

  if (loading) return <Box sx={{ p:4, textAlign:'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth:800, mx:'auto', p:2 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>My Applications</Typography>

      {/* Open intakes available to apply */}
      {openIntakes.length > 0 && (
        <Box sx={{ mb:3 }}>
          <Typography fontWeight={700} fontSize={13} color="primary" gutterBottom>🎓 Open Intakes — Apply Now</Typography>
          <Grid container spacing={2}>
            {openIntakes.map(intake => (
              <Grid item xs={12} sm={6} key={intake.id}>
                <Card variant="outlined" sx={{ borderRadius:2, border:'1px solid #2e7d32' }}>
                  <CardContent>
                    <Typography fontWeight={700}>{intake.title}</Typography>
                    <Typography fontSize={12} color="text.secondary">Grade: {intake.grade_applying}</Typography>
                    <Typography fontSize={12} color="text.secondary">Closes: {fmtD(intake.close_date)}</Typography>
                    {intake.description && <Typography fontSize={12} sx={{ mt:0.5 }} noWrap>{intake.description}</Typography>}
                    <Button fullWidth variant="contained" sx={{ mt:1.5, bgcolor:'#0f2b4a' }} onClick={() => onApply(intake)}>
                      Apply Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Existing applications */}
      {apps.map(app => {
        const cfg = STATUS_CFG[app.status] || STATUS_CFG.draft;
        const stepIdx = cfg.step || 0;
        return (
          <Card key={app.id} variant="outlined" sx={{ borderRadius:2, mb:2,
            border: app.status==='accepted' ? '2px solid #2e7d32' : app.status==='rejected' ? '1px solid #fca5a5' : undefined }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb:1 }}>
                <Box>
                  <Typography fontWeight={700}>{app.intake_title}</Typography>
                  <Typography fontSize={12} color="text.secondary">{app.grade_applying} · Applied: {fmtD(app.submitted_at)}</Typography>
                </Box>
                <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight:700 }} />
              </Stack>

              {app.status !== 'draft' && app.status !== 'withdrawn' && (
                <Stepper activeStep={stepIdx} alternativeLabel sx={{ mb:1.5 }}>
                  {STEPS.map(s => <Step key={s}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize:10 } }}>{s}</StepLabel></Step>)}
                </Stepper>
              )}

              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}><Typography fontSize={10} color="text.secondary">Documents</Typography><Typography fontSize={13} fontWeight={600}>{app.doc_count || 0} uploaded</Typography></Grid>
                {app.status === 'interview_scheduled' && <Grid item xs={12}><Alert severity="info" sx={{ fontSize:12 }}>Your interview has been scheduled. Please check your email for details.</Alert></Grid>}
                {app.status === 'accepted' && <Grid item xs={12}><Alert severity="success" sx={{ fontSize:12 }}>Congratulations! Your application has been accepted. The school will contact you with next steps.</Alert></Grid>}
                {app.status === 'rejected' && <Grid item xs={12}><Alert severity="error" sx={{ fontSize:12 }}>Thank you for applying. Unfortunately, your application was not successful this time.</Alert></Grid>}
              </Grid>

              <Stack direction="row" spacing={1} sx={{ mt:1 }} justifyContent="flex-end">
                {app.status === 'draft' && (
                  <Button size="small" variant="outlined" onClick={() => onEdit(app)}>Continue Application</Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {!apps.length && !openIntakes.length && (
        <Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}>
          <Typography color="text.secondary">No open intakes at this time. Check back later.</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ── Main public portal ─────────────────────────────────────────────────────────
export default function ApplicantPortal() {
  const [user, setUser]         = useState(null);
  const [token, setToken]       = useState(null);
  const [applying, setApplying] = useState(null);   // selected intake
  const [editApp, setEditApp]   = useState(null);   // existing app being edited
  const [key, setKey]           = useState(0);      // force refresh

  useEffect(() => {
    const t = localStorage.getItem('applicant_token');
    const u = localStorage.getItem('applicant_user');
    if (t && u) { setToken(t); setUser(JSON.parse(u)); }
  }, []);

  const handleLogin = (u, t) => {
    localStorage.setItem('applicant_token', t);
    localStorage.setItem('applicant_user', JSON.stringify(u));
    setUser(u); setToken(t);
  };

  const logout = () => {
    localStorage.removeItem('applicant_token');
    localStorage.removeItem('applicant_user');
    setUser(null); setToken(null); setApplying(null); setEditApp(null);
  };

  if (!user) return <AuthSection onLogin={handleLogin} />;

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f6fa' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#0f2b4a', px:3, py:1.5, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HowToReg sx={{ color:'white', fontSize:28 }} />
          <Box>
            <Typography color="white" fontWeight={700} fontSize={16}>School Admissions Portal</Typography>
            <Typography color="rgba(255,255,255,0.7)" fontSize={11}>Apply for a place at our school</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography color="rgba(255,255,255,0.8)" fontSize={12}>Welcome, {user.first_name}</Typography>
          <Button size="small" variant="outlined" onClick={logout} sx={{ color:'white', borderColor:'rgba(255,255,255,0.4)', fontSize:11 }}>Logout</Button>
        </Stack>
      </Box>

      {/* Content */}
      {applying || editApp ? (
        <ApplicationForm
          intake={applying || editApp}
          existingApp={editApp}
          token={token}
          onBack={() => { setApplying(null); setEditApp(null); setKey(k => k+1); }}
          onSaved={(submitted) => {
            if (submitted) { setApplying(null); setEditApp(null); setKey(k => k+1); }
            else setKey(k => k+1);
          }}
        />
      ) : (
        <MyApplications key={key} token={token}
          onApply={intake => setApplying(intake)}
          onEdit={app => { setEditApp(app); setApplying(null); }}
        />
      )}
    </Box>
  );
}
